import React, { useState, useEffect, useMemo } from "react";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import userBalanceReducer, { updateUserBalance } from "stores/balance/userBalance";
import CashierRedesign from "components/cashier-redesign/CashierRedesign";
import { CashierPreviewContext } from "components/cashier-redesign/previewContext";
import { setPreviewInert } from "components/cashier-redesign/useCashierData";

// The whole preview surface is inert w.r.t. live mutations: no PSP redirect, no
// crypto address, no bonus/limit/withdraw write ever reaches the backend.
setPreviewInert(true);

// Only the back-office builder origin may drive this iframe. postMessage from any
// other origin is ignored (defence in depth alongside the CSP frame-ancestors
// header on /cashier-preview).
const BUILDER_ORIGIN = "https://goldenpeace34.goldenclovercasino.com";

// Standalone entry for the /cashier-preview route. Renders NOTHING until the
// builder posts a CASHIER_PREVIEW_CONFIG; then it mounts the REAL CashierRedesign
// shell driven purely by that config (every data hook short-circuits its fetches
// via CashierPreviewContext, so no live API/PSP calls are ever made here).
export default function CashierPreviewRoot() {
  const [config, setConfig] = useState(null);

  // A private store so the preview never shares/mutates the app's real store.
  // Only the userBalance slice is needed (the cashier BALANCE reads it via redux).
  const previewStore = useMemo(
    () => configureStore({ reducer: { userBalance: userBalanceReducer } }),
    []
  );

  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== BUILDER_ORIGIN) return;
      if (!event.data || event.data.type !== "CASHIER_PREVIEW_CONFIG") return;
      setConfig(event.data);
      if (event.data.balance) {
        // The shell reads total/cash/bonus_balance from redux; a posted balance
        // that only carries wallets[] would write undefined over those fields
        // and pin the header at 0.00 — derive the missing figures from the
        // first wallet before dispatching.
        const b = event.data.balance;
        const walletAmount = Number(b.wallets && b.wallets[0] && b.wallets[0].balance) || 0;
        previewStore.dispatch(
          updateUserBalance({
            ...b,
            cash_balance: b.cash_balance != null ? b.cash_balance : walletAmount,
            bonus_balance: b.bonus_balance != null ? b.bonus_balance : 0,
            total_balance: b.total_balance != null ? b.total_balance : walletAmount,
          })
        );
      }
    };
    window.addEventListener("message", handler);
    // Handshake: tell the builder the iframe is ready to receive config.
    try {
      window.parent.postMessage({ type: "CASHIER_PREVIEW_READY" }, BUILDER_ORIGIN);
    } catch (e) {
      // parent unreachable (e.g. opened directly) -> stays on the empty state
    }
    return () => window.removeEventListener("message", handler);
  }, [previewStore]);

  // A quiet placeholder until the builder posts the first config (on the dark
  // cashier backdrop, so the panel never flashes white).
  if (!config) return <div style={{ position: "fixed", inset: 0, background: "#0a1f15" }} />;

  return (
    <Provider store={previewStore}>
      <CashierPreviewContext.Provider value={config}>
        <CashierRedesign open onClose={() => {}} initialTab={config.initialTab || "deposit"} />
      </CashierPreviewContext.Provider>
    </Provider>
  );
}
